import { Formik } from 'formik'
import PropTypes from 'prop-types'
import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import { ModalSidebar } from '../../../../../../components/ModalSidebar'
import { DefaultSettingsContext } from '../../../default-settings/DefaultSettingsProvider'
import { SettingsModalVisibilityContext } from '../../../default-settings/SettingsModalsVisibilityProvider'
import { DefaultSettingsSetupContext, useDefaultSettingsSetup } from '../DefaultSettingsSetup'

const propTypes = {
  /**
   * Modal content
   */
  children: PropTypes.node.isRequired,
  /**
   * Initial form values. Must be a constant or memoized
   */
  initialValues: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  /**
   *  Formik validation schema
   */
  validationSchema: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  /**
   * Steps for the ModalSidebar component
   * Each step should contain a label (node) and a key (string).
   */
  steps: PropTypes.array, // eslint-disable-line react/forbid-prop-types
  /**
   * Modal title. Formatted message id
   */
  title: PropTypes.string.isRequired,
  /**
   * Callback called whenever the setup company changes (with params: setupCompany)
   */
  onSetupCompanyChange: PropTypes.func,
}

/**
 * Wraps the form and sidebar logic for the default settings modals
 * It uses Formik, our ModalSidebar and default settings contexts to build the modal base
 */
function DefaultSettingsSidebar(props) {
  const {
    children,
    initialValues,
    validationSchema = undefined,
    steps = [],
    title,
    onSetupCompanyChange = () => {},
  } = props ?? {}

  const intl = useIntl()

  const {
    // create
    createEngageDefaultSettings,
    isCreatingEngageDefaultSettings,
    successCreatingEngageDefaultSettings,
    // update
    updateEngageDefaultSettings,
    isUpdatingEngageDefaultSettings,
    successUpdatingEngageDefaultSettings,
    // fetch
    fetchEngageDefaultSettings,
    engageDefaultSettings,
    // reset
    resetEngageDefaultSettingsControls,
  } = useContext(DefaultSettingsContext)

  const { hideSettingsModal, openedSettingsModal } = useContext(SettingsModalVisibilityContext)
  const { modalAction, initialSetupCompany, initialSetupType, headerBackButtonLabel } = openedSettingsModal

  const defaultSettingsSetup = useDefaultSettingsSetup({
    engageDefaultSettings,
    initialSetupCompany,
    initialSetupType,
    action: modalAction,
  })
  const { initialSettings, settingId, setupCompany } = defaultSettingsSetup

  const onSubmit = useCallback(
    async (values) => {
      resetEngageDefaultSettingsControls()

      if (settingId) {
        await updateEngageDefaultSettings({
          settingId,
          settings: values,
        })
      } else {
        await createEngageDefaultSettings({
          customerId: setupCompany,
          actionName: modalAction,
          settings: values,
        })
      }
      fetchEngageDefaultSettings()
    },
    [
      createEngageDefaultSettings,
      updateEngageDefaultSettings,
      fetchEngageDefaultSettings,
      settingId,
      setupCompany,
      resetEngageDefaultSettingsControls,
      modalAction,
    ],
  )

  // merge initial values with loaded settings
  // in case new fields are added in the future we fill the new fields with the correct values
  const loadedInitialValues = useMemo(
    () => ({ ...initialValues, ...initialSettings }),
    [initialValues, initialSettings],
  )

  // reset api metadata when opening the modal (e.g. post success, or put error)
  useEffect(() => {
    resetEngageDefaultSettingsControls()
  }, [resetEngageDefaultSettingsControls])

  // reset form if selected customer changed and there is no settings loaded for them
  const formikRef = useRef(null)
  useEffect(() => {
    if (!formikRef.current) {
      return // nothing to do if ref was not initialized yet
    }

    if (!initialSettings) {
      formikRef.current.resetForm({ values: initialValues })
    }
  }, [initialSettings, setupCompany, initialValues])

  // call onSetupCompanyChange if the setup company changes
  useEffect(() => {
    onSetupCompanyChange(setupCompany)
  }, [setupCompany, onSetupCompanyChange])

  return (
    <DefaultSettingsSetupContext.Provider value={defaultSettingsSetup}>
      <Formik
        innerRef={formikRef}
        initialValues={loadedInitialValues}
        // enableReinitialize flag forces the form to reset every time initial values change
        // it's needed here since the initial values depend on the selection in the setup
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({ dirty }) => (
          <ModalSidebar
            show // show is controlled externally
            onHide={hideSettingsModal}
            steps={steps}
            title={title}
            titlePillLabel="DEFAULT_SETTINGS.DEFAULT_SETTINGS"
            subTitles={[intl.formatMessage({ id: 'ENGAGE.MICROSOFT_365' })]}
            headerBackButtonLabel={headerBackButtonLabel || 'DEFAULT_SETTINGS.BACK_TO_DEFAULT_SETTINGS'}
            shouldShowCloseConfirmation={() =>
              dirty && !(successCreatingEngageDefaultSettings || successUpdatingEngageDefaultSettings)
            }
            isLoading={isCreatingEngageDefaultSettings || isUpdatingEngageDefaultSettings}
            loadingDescription="DEFAULT_SETTINGS.SAVING_DEFAULT_SETTINGS"
          >
            {children}
          </ModalSidebar>
        )}
      </Formik>
    </DefaultSettingsSetupContext.Provider>
  )
}

DefaultSettingsSidebar.propTypes = propTypes

export default DefaultSettingsSidebar
